/**
 * UnverifiedLeadsPage — lightweight placeholder for the Lead Manager's
 * unverified-inbox queue. Squad-A backend may not have shipped the
 * `verificationStatus=pending_verification` filter yet, so we degrade
 * gracefully on 404/500 and show the stub state.
 *
 * Apr 26, 2026 (Squad-B).
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Phone, ArrowLeft } from 'lucide-react';
import { safeGet } from '@/pages/dashboard/roles/_dashboardShared';

interface LeadRow {
  id: string;
  name?: string | null;
  customerName?: string | null;
  contactPhone?: string | null;
  phone?: string | null;
  source?: string | null;
  conversationState?: string | null;
  conversationStateUpdatedAt?: string | null;
  verificationStatus?: string | null;
  createdAt?: string | null;
}

function leadName(l: LeadRow): string {
  return l.customerName || l.name || 'Unknown lead';
}

function leadPhone(l: LeadRow): string {
  return l.contactPhone || l.phone || '—';
}

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const diffMs = Date.now() - t;
  const m = Math.round(diffMs / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.round(h / 24)}d`;
}

export function UnverifiedLeadsPage() {
  const [rows, setRows] = useState<LeadRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    safeGet<{ items?: LeadRow[]; data?: LeadRow[] } | LeadRow[]>(
      '/leads?verificationStatus=pending_verification&limit=50',
    ).then((r) => {
      const raw = r.data;
      const list: LeadRow[] = Array.isArray(raw)
        ? raw
        : (raw as { items?: LeadRow[]; data?: LeadRow[] })?.items
        ?? (raw as { items?: LeadRow[]; data?: LeadRow[] })?.data
        ?? [];
      setRows(list);
      setError(r.error);
      setLoading(false);
    });
  }, []);

  return (
    <div className="aura-root" style={{ padding: 24, background: 'var(--ivory-0)', minHeight: '100%' }}>
      <div style={{ marginBottom: 18, display: 'flex', alignItems: 'center', gap: 12 }}>
        <Link
          to="/dashboard"
          style={{
            color: 'var(--ink-3)',
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 12,
          }}
        >
          <ArrowLeft size={12} /> Back
        </Link>
      </div>

      <div style={{ marginBottom: 18 }}>
        <h2
          style={{
            fontFamily: 'var(--ff-serif)',
            fontSize: 22,
            fontWeight: 500,
            marginBottom: 2,
            color: 'var(--ink-0)',
          }}
        >
          Unverified leads
        </h2>
        <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
          Verify each lead before it can be assigned to a team.
        </div>
      </div>

      <div
        style={{
          background: 'var(--ivory-1)',
          border: '1px solid var(--line)',
          borderRadius: 10,
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <div style={{ padding: 28, fontSize: 13, color: 'var(--ink-3)', textAlign: 'center' }}>
            Loading…
          </div>
        ) : error || rows.length === 0 ? (
          <div style={{ padding: 36, textAlign: 'center' }}>
            <ShieldCheck size={28} style={{ color: 'var(--ink-3)', marginBottom: 8 }} />
            <div style={{ fontSize: 13, color: 'var(--ink-2)', fontWeight: 500, marginBottom: 4 }}>
              {error ? 'Verification queue not available yet' : 'No unverified leads'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', maxWidth: 360, margin: '0 auto' }}>
              {error
                ? 'Backend endpoint is not online. Squad-A is shipping the schema; once live, leads will appear here.'
                : 'Inbox is clear. New WhatsApp / IndiaMART / website leads land here for verification.'}
            </div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--ivory-2)', borderBottom: '1px solid var(--line)' }}>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 500, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                  Customer
                </th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 500, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                  Phone
                </th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 500, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                  Source
                </th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 500, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                  Drop-off
                </th>
                <th style={{ padding: '10px 16px', textAlign: 'right', fontSize: 11, fontWeight: 500, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((lead) => (
                <tr key={lead.id} style={{ borderBottom: '1px solid var(--line)' }}>
                  <td style={{ padding: '10px 16px', color: 'var(--ink-0)', fontWeight: 500 }}>
                    {leadName(lead)}
                  </td>
                  <td style={{ padding: '10px 16px', color: 'var(--ink-2)' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Phone size={11} style={{ color: 'var(--ink-3)' }} /> {leadPhone(lead)}
                    </span>
                  </td>
                  <td style={{ padding: '10px 16px', color: 'var(--ink-2)' }}>
                    {(lead.source ?? 'unknown').toLowerCase()}
                  </td>
                  <td style={{ padding: '10px 16px', color: 'var(--ink-2)' }}>
                    {lead.conversationState ?? '—'}
                    {lead.conversationStateUpdatedAt && (
                      <span style={{ color: 'var(--ink-3)' }}> ({relativeTime(lead.conversationStateUpdatedAt)})</span>
                    )}
                  </td>
                  <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                    <button
                      type="button"
                      className="btn btn-outline"
                      style={{ fontSize: 11, padding: '4px 10px' }}
                      onClick={() => {
                        // TODO: open verify drawer once backend ships POST /leads/:id/verify.
                        alert(`Verify lead ${lead.id} — coming soon`);
                      }}
                    >
                      Verify
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default UnverifiedLeadsPage;
